
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IndiceDTOList } from '../Models/IndiceDTO';
import { Button } from '../Components/ui/button';
import { useTranslation } from 'react-i18next';
import { useAuth } from "../Auth/autoContext";
import { useNavigate } from "react-router-dom";
import { clearAllCache } from "../Data/dataCache";
import { FaAngleDoubleLeft, FaAngleLeft, FaAngleRight, FaAngleDoubleRight } from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";
import { FaStar, FaRegStar } from "react-icons/fa";
import { getFavoriteSymbols, toggleFavorite } from "../Data/dataApi";

type Props = {
  fetchData: (
    search: string,
    exchange: string,
    sort: string,
    page: number,
    pageSize: number
  ) => Promise<any>;
  returnUrl: string; // Ou tu peux typer selon la structure retournée
  saveCache?: (data: { items: any[]; totalPages: number }, search: string, exchange: string, sort: string, page: number, role: string) => Promise<void>;
  getCachedData?: (search: string, exchange: string, sort: string, page: number, role: string) => Promise<{ items: any[]; totalPages: number } | null>;
};

const IndiceExplorer: React.FC<Props> = ({ fetchData, returnUrl, getCachedData, saveCache }) => {
  const [indices, setIndices] = useState<IndiceDTOList[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [exchangeFilter, setExchangeFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { role } = useAuth();
  const previousRoleRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const [doSearch, setDoSearch] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (previousRoleRef.current && previousRoleRef.current !== role) {
      clearAllCache();
    }

    previousRoleRef.current = role;

  }, [role]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1140);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {

    const fetchAndCache = async () => {
      setIsLoading(true);

      const cachedData =
        returnUrl !== "favorites" &&
          getCachedData &&
          searchTerm === '' &&
          exchangeFilter === ''
          ? await getCachedData(searchTerm, exchangeFilter, sortOrder, page, role)
          : null;

      if (cachedData) {
        setIndices(cachedData.items);
        setTotalPages(cachedData.totalPages);
      } else {
        try {
          const data = await fetchData(searchTerm, exchangeFilter, sortOrder, page, pageSize);

          setIndices(data.items || []);
          setTotalPages(data.totalPages || 1);

          if (returnUrl !== "favorites" && saveCache) {
            await saveCache(data, searchTerm, exchangeFilter, sortOrder, page, role);
          }
        } catch (err) {
          console.error('Erreur chargement indices', err);
        }
      }

      setIsLoading(false);
    };

    fetchAndCache();
  }, [doSearch, searchTerm, exchangeFilter, sortOrder, page, pageSize, fetchData, getCachedData, saveCache, role, returnUrl]);

  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    const load = async () => {
      const favs = await getFavoriteSymbols();
      setFavorites(favs);
    };

    load();
  }, []);

  const isFavorite = (symbol: string) => {
    return favorites.includes(symbol);
  };

  const handleFavorite = async (symbol: string) => {
    const isFav = favorites.includes(symbol);

    try {
      await toggleFavorite(symbol, isFav);

      await clearAllCache();

      const updated = isFav
        ? favorites.filter(x => x !== symbol)
        : [...favorites, symbol];

      setFavorites(updated);

      if (returnUrl === "favorites") {
        const data = await fetchData(
          searchTerm,
          exchangeFilter,
          sortOrder,
          page,
          pageSize
        );

        setIndices(data.items);
        setTotalPages(data.totalPages);
      }

    } catch (e) {
      console.error(e);
    }
  };

  const handleRefreshCache = async () => {
    setIsLoading(true);

    try {
      await clearAllCache(); // efface IndexedDB

      const data = await fetchData(searchTerm, exchangeFilter, sortOrder, page, pageSize);

      setIndices(data.items || []);
      setTotalPages(data.totalPages || 1);

      if (returnUrl !== "favorites" && saveCache) {
        await saveCache(data, searchTerm, exchangeFilter, sortOrder, page, role);
      }
    } catch (err) {
      console.error("Erreur refresh cache", err);
    }

    setIsLoading(false);
  };

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPage(1);
    if (value === '') {
      setDoSearch(true); // reset auto quand input est vidé
    }
  };

  const handleExchangeFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setExchangeFilter(e.target.value);
    setSearchTerm('');   // 🔥 efface le champ de recherche
    setPage(1);
  };

  const handleSort = (key: string) => {
    const newSortOrder = (prev: string) => {
      switch (key) {
        case 'symbol':
          return prev === 'symbol_desc' ? 'symbol_asc' : 'symbol_desc';
        case 'price':
          return prev === 'price_desc' ? 'price_asc' : 'price_desc';
        case 'change':
          return prev === 'change_asc' ? 'change_desc' : 'change_asc';
        case 'exercfinanc':
          return prev === 'exercfinanc_asc' ? 'exercfinanc_desc' : 'exercfinanc_asc';
        case 'bourse':
          return prev === 'bourse_asc' ? 'bourse_desc' : 'bourse_asc';
        case 'label':
          return prev === 'label_asc' ? 'label_desc' : 'label_asc';
        case 'prob':
          return prev === 'prob_desc' ? 'prob_asc' : 'prob_desc';
        case 'strongmove':
          return prev === 'strongmove_desc' ? 'strongmove_asc' : 'strongmove_desc';
        default:
          return prev;
      }
    };
    setSortOrder(newSortOrder(sortOrder));
    setPage(1); // Remet à la première page pour chaque changement de tri
  };

  const getRecommendationClass = (rec: string): string => {
    switch (rec) {
      case 'Strong Buy': return 'text-strong-buy';
      case 'Buy': return 'text-buy';
      case 'Hold': return 'text-hold';
      case 'Sell': return 'text-sell';
      case 'Strong Sell': return 'text-strong-sell';
      default: return '';
    }
  };

  const formatDate = (dates: Date[]): string[] => {
    return dates.map(dateStr => {
      const date = new Date(dateStr);
      return date instanceof Date && !isNaN(date.getTime())
        ? date.toLocaleDateString('fr', { timeZone: 'UTC' })
        : "Date invalide";
    });
  };

  const renderMobileCards = () => (
    <div className="card-container">
      {indices.map((indice, index) => (
        <div key={index} className="indice-card">

          <div className="card-header" id="text-mobile">
            <div>
              <div>{indice.symbol}</div>
              <div>{indice.name}</div>
            </div>

            <button
              className="favorite-button"
              onClick={() => handleFavorite(indice.symbol)}
            >
              {isFavorite(indice.symbol)
                ? <FaStar color="gold" />
                : <FaRegStar />}
            </button>
          </div>

          <div className="card-row" id='text-mobile'>
            <span>{t('list.table.price')}</span>
            <span>{indice.regularMarketPrice?.toFixed(3)}</span>
          </div>

          <div className="card-row">
            <span>{t('list.table.close')}</span>
            <span>{indice.regularMarketPreviousClose?.toFixed(3)}</span>
          </div>

          <div className="card-row">
            <span>{t('list.table.change')}</span>
            <span className={indice.regularMarketChange > 0 ? "color-positive" : "color-negative"}>
              {indice.regularMarketChange?.toFixed(3)}
            </span>
          </div>

          {/* <div className="card-row">
            <span>{t('list.table.type')}</span>
            <span>{indice.quoteType}</span>
          </div> */}

          {role === 'premium' && (
            <>
              <div className="card-row">
                <span>{t('list.table.dates')}</span>
                <span>{indice.datesExercicesFinancieres && indice.datesExercicesFinancieres.length > 0 ? (
                  indice.datesExercicesFinancieres.map((date, i) => {
                    const isMinDate = new Date(date).getFullYear() === 1; // Vérifie si c'est la date min
                    return (
                      <span key={i} className="block">
                        {isMinDate ? 'N/A' : formatDate([new Date(date)])}<br />
                      </span>
                    );
                  })
                ) : (
                  <span>N/A</span> // S'il n'y a aucune date
                )}
                </span>
              </div>
            </>
          )}

          <div className="card-row">
            <span>{t('list.table.exchange')}</span>
            <span>{indice.bourse}</span>
          </div>
{/* 
          <div className="card-row">
            <span>{t('list.table.volume')}</span>
            <span>{indice.regularMarketVolume}</span>
          </div> */}

          <div className="card-row">
            <span>{t('list.table.label')}</span>
            <span className={indice.label ? "color-positive" : "color-negative"}>{indice.label ? "UP" : "DOWN"}</span>
          </div>

          {role === 'premium' && (
            <>
              <div className="card-row">
                <span>{t('list.table.recommendation')}</span>
                <span className={`rec-cell ${getRecommendationClass(indice.raccomandation)}`}>{indice.raccomandation}</span>
              </div>
            </>
          )}

          <div className="card-row">
            <span>{t('list.table.updated')}</span>
            <span>{new Date(indice.dateUpdated).toLocaleDateString('fr', { timeZone: 'UTC' })}</span>
          </div>

          {role === 'premium' && (
            <>
              <div className="card-row">
                <span>{t('list.table.probability')}</span>
                <span className={indice.probability > 0.49 ? "color-positive" : "color-negative"}>{(indice.probability * 100).toFixed(1)}%</span>
              </div>

              <div className="card-row">
                <span>{t('list.table.isincreasing')}</span>
                <span className={indice.isIncreasing === null ? "" : indice.isIncreasing ? "color-positive" : "color-negative"}>{indice.isIncreasing === null ? "N/A" : indice.isIncreasing ? t('list.table.yes') : t('list.table.no')}</span>
              </div>

              <div className="card-row">
                <span>{t('list.table.strongmove')}</span>
                <span className={indice.strongMove ? "color-positive" : "color-negative"}>{indice.strongMove ? t('list.table.yes') : t('list.table.no')}</span>
              </div>

              <div className="card-row">
                <span>{t('list.table.strongmoveprobability')}</span>
                <span className={indice.strongMoveProbability > 0.49 ? "color-positive" : "color-negative"}>{(indice.strongMoveProbability * 100).toFixed(1)}%</span>
              </div>

            </>
          )}

          <div className="card-actions">
              <Link to={`/stock/${indice.symbol}?returnUrl=${returnUrl}`}>
                <button className="button-details">Details</button>
              </Link>
          </div>

        </div>
      ))}
    </div>
  );

  return (
    <div className='list-container'>
      <div className='list-filter'>
        <select
          value={exchangeFilter}
          onChange={handleExchangeFilter}
        >
          <option value="">{t('list.filter')}</option>
          <option value="TOR">Toronto</option>
          <option value="NYC">New York</option>
        </select>

        <div className="search-box">
          <input
            type="text"
            placeholder={t('list.search')}
            className="list-search"
            value={searchTerm}
            onChange={handleSearchInput}
          />

          {searchTerm && (
            <button
              className="button-cancel"
              onClick={() => {
                setSearchTerm('');
                setPage(1);
                setDoSearch(true);
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="pagination-wrapper">
        <div className="pagination">

          {/* Premier */}
          <Button
            onClick={() => setPage(1)}
            disabled={page === 1}>
            <FaAngleDoubleLeft />
          </Button>

          {/* Précédent */}
          <Button
            onClick={() => setPage(p => Math.max(p - 1, 1))}
            disabled={page === 1}>
            <FaAngleLeft />
          </Button>

          {/* Page info */}
          <span>{t('list.page')}</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={page}
            onChange={(e) => {
              let value = Number(e.target.value);

              if (value < 1) value = 1;
              if (value > totalPages) value = totalPages;

              setPage(value);
            }}
            className="page-input"
          />
          <span>{t('list.on')} {totalPages}</span>


          {/* Suivant */}
          <Button
            onClick={() => {
              setPage(p => {
                const newPage = Math.min(p + 1, totalPages);
                return newPage;
              });
            }}
            // label={t('list.button-next')}
            disabled={page === totalPages}>
            <FaAngleRight />
          </Button>

          {/* Dernier */}
          <Button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}>
            <FaAngleDoubleRight />
          </Button>
        </div>
        <button className="button-refresh" onClick={handleRefreshCache}>
          <FiRefreshCw style={{ marginRight: 6 }} />
          Refresh
        </button>
      </div>

      {isMobile ? renderMobileCards() : (
        <table className="table">
          <thead>
            <tr>
              <th></th>
              <th className='orderColumn' onClick={() => handleSort('symbol')}>{t('list.table.symbol')}
                {sortOrder === 'symbol_asc' || sortOrder === 'symbol_desc' ? (
                  <span style={{ marginLeft: '5px' }}>
                    {sortOrder.endsWith('_desc') ? '🔽' :
                      sortOrder.endsWith('_asc') ? '🔼' : '⇅'}
                  </span>
                ) : (
                  <span style={{ marginLeft: '5px', color: 'darkslategray' }}>
                    ⇅
                  </span>
                )}
              </th>
              <th >{t('list.table.name')}</th>
              <th className='orderColumn' onClick={() => handleSort('price')}>{t('list.table.price')}
                {sortOrder === 'price_asc' || sortOrder === 'price_desc' ? (
                  <span style={{ marginLeft: '5px' }}>
                    {sortOrder.endsWith('_desc') ? '🔽' :
                      sortOrder.endsWith('_asc') ? '🔼' : '⇅'}
                  </span>
                ) : (
                  <span style={{ marginLeft: '5px', color: 'darkslategray' }}>
                    ⇅
                  </span>
                )}
              </th>
              <th >{t('list.table.close')}</th>
              <th className='orderColumn' onClick={() => handleSort('change')}>{t('list.table.change')}
                {sortOrder === 'change_asc' || sortOrder === 'change_desc' ? (
                  <span style={{ marginLeft: '5px' }}>
                    {sortOrder.endsWith('_desc') ? '🔽' :
                      sortOrder.endsWith('_asc') ? '🔼' : '⇅'}
                  </span>
                ) : (
                  <span style={{ marginLeft: '5px', color: 'darkslategray' }}>
                    ⇅
                  </span>
                )}
              </th>
              {/* <th >{t('list.table.type')}</th> */}
              {role === 'premium' && (
                <>
                  <th className='orderColumn' onClick={() => handleSort('exercfinanc')}>{t('list.table.dates')}
                    {sortOrder === 'exercfinanc_asc' || sortOrder === 'exercfinanc_desc' ? (
                      <span style={{ marginLeft: '5px' }}>
                        {sortOrder.endsWith('_desc') ? '🔽' :
                          sortOrder.endsWith('_asc') ? '🔼' : '⇅'}
                      </span>
                    ) : (
                      <span style={{ marginLeft: '5px', color: 'darkslategray' }}>
                        ⇅
                      </span>
                    )}
                  </th>
                </>
              )}
              <th className='orderColumn' onClick={() => handleSort('bourse')}>{t('list.table.exchange')}
                {sortOrder === 'bourse_asc' || sortOrder === 'bourse_desc' ? (
                  <span style={{ marginLeft: '5px' }}>
                    {sortOrder.endsWith('_desc') ? '🔽' :
                      sortOrder.endsWith('_asc') ? '🔼' : '⇅'}
                  </span>
                ) : (
                  <span style={{ marginLeft: '5px', color: 'darkslategray' }}>
                    ⇅
                  </span>
                )}
              </th>
              {/* <th>{t('list.table.volume')}</th> */}
              {/* <th className='orderColumn' onClick={() => handleSort('label')}>{t('list.table.label')}
                {sortOrder === 'label_asc' || sortOrder === 'label_desc' ? (
                  <span style={{ marginLeft: '5px' }}>
                    {sortOrder.endsWith('_desc') ? '🔽' :
                      sortOrder.endsWith('_asc') ? '🔼' : '⇅'}
                  </span>
                ) : (
                  <span style={{ marginLeft: '5px', color: 'darkslategray' }}>
                    ⇅
                  </span>
                )}
              </th> */}
              {role === 'premium' && (
                <>
                  <th>{t('list.table.recommendation')}</th>
                </>
              )}
              <th id='update'>{t('list.table.updated')}</th>
              {role === 'premium' && (
                <>
                  <th className='orderColumn' onClick={() => handleSort('prob')}>{t('list.table.probability')}
                    {sortOrder === 'prob_asc' || sortOrder === 'prob_desc' ? (
                      <span style={{ marginLeft: '5px' }}>
                        {sortOrder.endsWith('_desc') ? '🔽' :
                          sortOrder.endsWith('_asc') ? '🔼' : '⇅'}                        
                      </span>
                    ) : (
                      <span style={{ marginLeft: '5px', color: 'darkslategray' }}>
                        ⇅
                      </span>
                    )}
                  </th>
                  <th>{t('list.table.isincreasing')}</th>
                  <th className='orderColumn' onClick={() => handleSort('strongmove')}>{t('list.table.strongmove')}
                    {sortOrder === 'strongmove_asc' || sortOrder === 'strongmove_desc' ? (
                      <span style={{ marginLeft: '5px' }}>
                        {sortOrder.endsWith('_desc') ? '🔽' : 
                          sortOrder.endsWith('_asc') ? '🔼' : '⇅'}
                      </span>
                    ) : (
                      <span
                        style={{
                          marginLeft: '5px',
                          color: 'darkslategray'
                        }}
                      >
                        ⇅
                      </span>
                    )}
                  </th>
                  <th>{t('list.table.strongmoveprobability')}</th>
                </>
              )}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr className='chargement'>
                <td colSpan={13} className="chargement-list">
                  <div className="spinner"></div><br /> {/* Ajoute ici ton spinner */}
                  {t('list.loading')} {/* ou un spinner ici */}
                </td>
              </tr>
            ) : indices.length === 0 ? (
              <tr>
                <td colSpan={13} className="chargement-list">
                  {t('list.resultat')} {/* Aucun résultat */}
                </td>
              </tr>
            ) : (
              indices.map((indice, index) => (
                <tr key={index} className="border-t">
                  <td>
                    <button
                      className="favorite-button"
                      onClick={() => handleFavorite(indice.symbol)}
                    >
                      {isFavorite(indice.symbol)
                        ? <FaStar />
                        : <FaRegStar />}
                    </button>
                  </td>
                  <td className='textbold'>{indice.symbol}</td>
                  <td className='textbold'>{indice.name}</td>
                  <td>{indice.regularMarketPrice?.toFixed(3)}</td>
                  <td>{indice.regularMarketPreviousClose?.toFixed(3)}</td>            
                  <td className={indice.regularMarketChange > 0 ? "color-positive" : "color-negative"}>{indice.regularMarketChange?.toFixed(3)}</td>
                  {/* <td>{indice.quoteType}</td> */}
                  {role === 'premium' && (
                    <>
                      <td>
                        {indice.datesExercicesFinancieres && indice.datesExercicesFinancieres.length > 0 ? (
                          indice.datesExercicesFinancieres.map((date, i) => {
                            const isMinDate = new Date(date).getFullYear() === 1; // Vérifie si c'est la date min
                            return (
                              <span key={i} className="block">
                                {isMinDate ? 'N/A' : formatDate([new Date(date)])}<br />
                              </span>
                            );
                          })
                        ) : (
                          <span>N/A</span> // S'il n'y a aucune date
                        )}
                      </td>
                    </>
                  )}
                  <td>{indice.bourse}</td>
                  {/* <td>{indice.regularMarketVolume}</td> */}
                  {/* <td className={indice.label ? "color-positive" : "color-negative"}>{indice.label ? "UP" : "DOWN"}</td> */}
                  {role === 'premium' && (
                    <>
                      <td className={`rec-cell ${getRecommendationClass(indice.raccomandation)}`}>{indice.raccomandation}</td>
                    </>
                  )}
                  <td>{new Date(indice.dateUpdated).toLocaleDateString('fr', { timeZone: 'UTC' })}</td>
                  {role === 'premium' && (
                    <>
                      <td className={indice.probability > 0.49 ? "color-positive" : "color-negative"}>{(indice.probability * 100).toFixed(1)}%</td>
                      <td className={indice.isIncreasing === null ? "" : indice.isIncreasing ? "color-positive" : "color-negative"}>{indice.isIncreasing === null ? "N/A" : indice.isIncreasing ? t('list.table.yes') : t('list.table.no')}</td>
                      <td className={indice.strongMove ? "color-positive" : "color-negative"}>{indice.strongMove ? t('list.table.yes') : t('list.table.no')}</td>
                      <td className={indice.strongMoveProbability > 0.49 ? "color-positive" : "color-negative"}>{(indice.strongMoveProbability * 100).toFixed(1)}%</td>
                    </>
                  )}
                  <td id='column-details'>
                      <Link
                        to={`/stock/${indice.symbol}?returnUrl=${returnUrl}`}
                        className="button-details"
                      >
                        Details
                      </Link>
                  </td>
                </tr>
              )))}
          </tbody>
        </table>
      )}

      <div className="pagination-wrapper">
        <div className="pagination">

          {/* Premier */}
          <Button
            onClick={() => setPage(1)}
            disabled={page === 1}>
            <FaAngleDoubleLeft />
          </Button>

          {/* Précédent */}
          <Button
            onClick={() => setPage(p => Math.max(p - 1, 1))}
            disabled={page === 1}>
            <FaAngleLeft />
          </Button>

          {/* Page info */}
          <span>{t('list.page')}</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={page}
            onChange={(e) => {
              let value = Number(e.target.value);

              if (value < 1) value = 1;
              if (value > totalPages) value = totalPages;

              setPage(value);
            }}
            className="page-input"
          />
          <span>{t('list.on')} {totalPages}</span>


          {/* Suivant */}
          <Button
            onClick={() => {
              setPage(p => {
                const newPage = Math.min(p + 1, totalPages);
                return newPage;
              });
            }}
            // label={t('list.button-next')}
            disabled={page === totalPages}>
            <FaAngleRight />
          </Button>

          {/* Dernier */}
          <Button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}>
            <FaAngleDoubleRight />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IndiceExplorer;
